import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import homepageTranslations from '../../translations';
import { isHomepageDemoLauncherEnabled } from '../../homepageDemoLauncherEnabled';
import DemoSDKContainer from 'components/sdk/DemoSDKContainer';
import { Icons } from 'coreIndex';
import './DemoSection.css';

/**
 * Animated background canvas for demo intro
 */
// eslint-disable-next-line no-unused-vars
const AnimatedBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    const drawGrid = () => {
      const width = canvas.width / (window.devicePixelRatio || 1);
      const height = canvas.height / (window.devicePixelRatio || 1);
      
      ctx.clearRect(0, 0, width, height);
      
      // Subtle grid pattern
      ctx.strokeStyle = 'rgba(88, 151, 222, 0.08)';
      ctx.lineWidth = 1;
      
      const gridSize = 60;
      const offsetX = (time * 0.02) % gridSize;
      const offsetY = (time * 0.01) % gridSize;
      
      // Vertical lines
      for (let x = -gridSize + offsetX; x < width + gridSize; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      
      // Horizontal lines
      for (let y = -gridSize + offsetY; y < height + gridSize; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      
      // Floating dots at intersections
      ctx.fillStyle = 'rgba(88, 151, 222, 0.12)';
      for (let x = -gridSize + offsetX; x < width + gridSize; x += gridSize) {
        for (let y = -gridSize + offsetY; y < height + gridSize; y += gridSize) {
          const pulseSize = 1.5 + Math.sin(time * 0.003 + x * 0.01 + y * 0.01) * 0.5;
          ctx.beginPath();
          ctx.arc(x, y, pulseSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const animate = () => {
      time += 1;
      drawGrid();
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();

    window.addEventListener('resize', resizeCanvas);
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return <canvas ref={canvasRef} className="demo-intro-canvas" aria-hidden="true" />;
};

/**
 * Demo Section Component
 * Two-phase component: Product introduction first, then interactive demo on button click
 */
const DemoSection = () => {
  const { language } = useTranslation();
  const demoLauncherEnabled = isHomepageDemoLauncherEnabled();
  const [showDemo, setShowDemo] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const introRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const demoRef = useRef(null);
  const sectionRef = useRef(null);
  
  const t = (key, defaultValue = '') => {
    const pageTranslations = homepageTranslations[language] || homepageTranslations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations.customerHomepage?.demoSection || pageTranslations.partnerHomepage?.demoSection || {});
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  const handleTestNow = () => {
    setShowDemo(true);
    // Smooth scroll to demo after transition
    setTimeout(() => {
      const demoElement = document.getElementById('demo-section');
      if (demoElement) {
        demoElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  const handleBackToOverview = () => {
    setShowDemo(false);
    // Smooth scroll back to intro
    setTimeout(() => {
      const demoSection = document.getElementById('demo-section');
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Scroll intersection observer for fade in/out
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.1,
        rootMargin: '-100px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Icon component helper
  const renderIcon = (iconName, size = 24) => {
    const IconComponent = Icons[iconName];
    if (IconComponent) {
      return <IconComponent size={size} />;
    }
    // Fallback icons for missing ones
    const fallbackIcons = {
      Globe: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
      ),
      Smartphone: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>
        </svg>
      ),
      RefreshCw: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 4 23 10 17 10"/>
          <polyline points="1 20 1 14 7 14"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
      ),
      Image: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
      ),
      Sliders: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="4" y1="21" x2="4" y2="14"/>
          <line x1="4" y1="10" x2="4" y2="3"/>
          <line x1="12" y1="21" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12" y2="3"/>
          <line x1="20" y1="21" x2="20" y2="16"/>
          <line x1="20" y1="12" x2="20" y2="3"/>
          <line x1="1" y1="14" x2="7" y2="14"/>
          <line x1="9" y1="8" x2="15" y2="8"/>
          <line x1="17" y1="16" x2="23" y2="16"/>
        </svg>
      ),
      BarChart: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 13h2v8H3v-8zm4-6h2v14H7V7zm4-4h2v18h-2V3zm4 8h2v10h-2V11zm4-6h2v16h-2V5z"/>
        </svg>
      ),
      Headphones: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
        </svg>
      ),
      Plug: () => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22v-5M9 8V2M15 8V2M6 12h12a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2z"/>
        </svg>
      )
    };
    const FallbackIcon = fallbackIcons[iconName];
    return FallbackIcon ? <FallbackIcon /> : null;
  };

  // Feature cards data
  const features = [
    {
      icon: 'Globe',
      title: t('features.websiteImplementation.title', 'Website Implementation'),
      description: t('features.websiteImplementation.description', 'Embeddable chatbot that integrates seamlessly into your website')
    },
    {
      icon: 'Smartphone',
      title: t('features.mobileFriendly.title', 'Mobile-Friendly'),
      description: t('features.mobileFriendly.description', 'Fully responsive design that works perfectly on all devices')
    },
    {
      icon: 'RefreshCw',
      title: t('features.selfMaintaining.title', 'Self-Maintaining'),
      description: t('features.selfMaintaining.description', 'Automatically updates knowledge from your website daily')
    },
    {
      icon: 'Image',
      title: t('features.fileUpload.title', 'Image & File Support'),
      description: t('features.fileUpload.description', 'Upload images and files - the bot uses them in contextual answers')
    },
    {
      icon: 'Sliders',
      title: t('features.customizable.title', 'Customizable Responses'),
      description: t('features.customizable.description', 'Adjust how it responds in detail through our Instructions UI')
    },
    {
      icon: 'BarChart',
      title: t('features.monitoring.title', 'Activity Monitoring'),
      description: t('features.monitoring.description', 'Monitor activity via dashboard or API')
    }
  ];

  const upcomingFeatures = [
    {
      icon: 'Headphones',
      title: t('upcomingFeatures.liveSupport.title', 'Live Support'),
      description: t('upcomingFeatures.liveSupport.description', 'Take over conversations in real-time to provide human assistance')
    },
    {
      icon: 'Plug',
      title: t('upcomingFeatures.apiTools.title', 'API Tools Integration'),
      description: t('upcomingFeatures.apiTools.description', 'Implement various APIs such as making reservations and more')
    }
  ];

  return (
    <>
      {/* Vertical Carousel Button - Fixed on right edge, fades with scroll */}
      {demoLauncherEnabled && !showDemo && (
        <div className={`demo-carousel-button-container ${isVisible ? 'visible' : ''}`}>
          <button 
            className="demo-try-button"
            onClick={handleTestNow}
            aria-label="View demo"
          >
            <span className="demo-try-button-text">
              {t('cta.testNow', 'Try It Free Now')}
            </span>
            <div className="demo-carousel-indicator">
              <span className="demo-indicator-dot active"></span>
              <span className="demo-indicator-dot"></span>
            </div>
          </button>
        </div>
      )}

      {/* Back Button - Fixed on left edge when demo is shown, fades with scroll */}
      {demoLauncherEnabled && showDemo && (
        <div className={`demo-back-button-container ${isVisible ? 'visible' : ''}`}>
          <button 
            className="demo-back-button-vertical"
            onClick={handleBackToOverview}
            aria-label="Back to overview"
          >
            <span className="demo-try-button-text">
              {t('cta.backToOverview', 'Back')}
            </span>
            <div className="demo-carousel-indicator">
              <span className="demo-indicator-dot"></span>
              <span className="demo-indicator-dot active"></span>
            </div>
          </button>
        </div>
      )}

      <section className="demo-section" id="bot-demo" ref={sectionRef}>
        <div className="demo-section-container">
          <div className={`demo-carousel-wrapper ${showDemo ? 'show-demo' : ''}`}>
            {/* Intro Content Panel */}
            <div className="demo-carousel-panel demo-intro-panel">
              <div className="demo-intro-content">
              <div className="demo-intro-header">
                <p className="ds-eyebrow">B-ESPER BOT</p>
                <h2 className="demo-intro-title">
                  {t('subtitle', 'An intelligent chatbot that answers customer questions instantly, 24/7. Embed it on your website in minutes—no coding required. It learns from your content daily and stays up to date.')}
                </h2>
              </div>

              <div className="demo-features-grid">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="demo-feature-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="demo-feature-icon">
                      {renderIcon(feature.icon, 24)}
                    </div>
                    <h4 className="demo-feature-title">{feature.title}</h4>
                    <p className="demo-feature-description">{feature.description}</p>
                  </div>
                ))}
              </div>

              <div className="demo-upcoming-section">
                <div className="demo-upcoming-header">
                  <h3 className="demo-upcoming-title">
                    {t('upcomingFeatures.title', 'Coming Soon')}
                  </h3>
                  <Link to="/roadmap" className="demo-upcoming-roadmap-link">
                    <span>{t('upcomingFeatures.viewRoadmap', 'View Roadmap')}</span>
                    <span className="demo-upcoming-roadmap-arrow">→</span>
                  </Link>
                </div>
                <div className="demo-upcoming-grid">
                  {upcomingFeatures.map((feature, index) => (
                    <div 
                      key={index}
                      className="demo-upcoming-card"
                      style={{ animationDelay: `${(features.length + index) * 0.1}s` }}
                    >
                      <div className="demo-upcoming-icon">
                        {renderIcon(feature.icon, 20)}
                      </div>
                      <h4 className="demo-upcoming-feature-title">
                        <Link to="/roadmap" className="demo-upcoming-link">
                          {feature.title}
                        </Link>
                      </h4>
                      <p className="demo-upcoming-feature-description">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </div>

            {/* Demo SDK Panel */}
            <div className="demo-carousel-panel demo-sdk-panel">
              <div className="demo-sdk-wrapper">
                <div className="demo-sdk-header">
                  <div className="demo-sdk-header-info">
                  </div>
                </div>
                <div className="demo-section-content">
                  <DemoSDKContainer 
                    containerId="homepage-demo-container"
                    className="homepage-demo-sdk"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DemoSection;

